import React from "react";
import mapboxgl from "mapbox-gl";
import { connect } from "react-redux";
import uuid from "uuid/v4";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { fetchGeoStats } from "./../../../action_managers/stats";
import { mapboxToken } from "./../../../constants/mapbox_token";
import { ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { blues } from "../../../lib/design";
import "./mapbox.scss";

mapboxgl.accessToken = mapboxToken;
const startingPoint = {
  location: [-98.962204, 38.847779],
  zoom: 3,
};
class Map extends React.Component {
  state = {
    toggleValue: "cluster",
  };

  componentDidMount() {
    this.map = new mapboxgl.Map({
      container: this.mapContainer,
      style: "mapbox://styles/mapbox/streets-v9",
      center: startingPoint.location,
      zoom: startingPoint.zoom,
      minZoom: 3,
      maxZoom: 6,
    });

    this.map.on("load", () => {
      this.map.addSource("downloads", {
        type: "geojson",
        data: this.sourceData(this.props),
      });

      this.map.addSource("downloads-cluster", {
        type: "geojson",
        data: this.sourceData(this.props),
        cluster: true,
        clusterRadius: 60, // Radius of each cluster when clustering points (defaults to 50)
        clusterProperties: {
          total_count: ["+", ["get", "count"]],
        },
      });

      this.map.addLayer(this.getHeatmapData());
      this.addClusterLayers();
      this.map.on("moveend", this.fetchNewData);
      this.fetchNewData();
    });
  }

  fetchNewData = (e, props = this.props) => {
    const bounds = this.map.getBounds();
    const { lng: NWLng, lat: NWLat } = bounds.getNorthWest();
    const { lng: SELng, lat: SELat } = bounds.getSouthEast();
    const existingFilters = props.filters;

    const query = `${NWLat},${NWLng},${SELat},${SELng}`;
    this.props.fetchGeoStats({ ...existingFilters, geoBoundingBox: query }, this.newGeoRequestID());
  };

  addClusterLayers = () => {
    this.map.addLayer(this.getClusterCircleAggData());
    this.map.addLayer(this.getClusterCircleData());
    this.map.addLayer(this.getClusterCountData("cluster-total-count", "total_count"));
    this.map.addLayer(this.getClusterCountData("cluster-count", "count"));
  };

  getClusterCircleData = () => {
    return {
      id: "cluster-circle",
      type: "circle",
      source: "downloads-cluster",
      filter: ["!", ["has", "total_count"]],
      paint: this.getClusterCirclePaint("count"),
    };
  };

  getClusterCircleAggData = () => {
    return {
      id: "cluster-total-circle",
      type: "circle",
      source: "downloads-cluster",
      filter: ["has", "total_count"],
      paint: this.getClusterCirclePaint("total_count"),
    };
  };

  getClusterCirclePaint = valueName => {
    return {
      "circle-color": [
        "step",
        ["get", valueName],
        blues[0],
        100,
        blues[1],
        1000,
        blues[2],
        10000,
        blues[3],
      ],
      "circle-radius": [
        "interpolate",
        ["linear", 50],
        ["get", valueName],
        1,
        1,
        100,
        20,
        1000,
        30,
        10000,
        40,
      ],
    };
  };

  getClusterCountData = (id, valueName) => {
    return {
      id: id,
      type: "symbol",
      source: "downloads-cluster",
      filter: ["has", valueName],
      layout: {
        "text-field": ["number-format", ["get", valueName], {}],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      },
    };
  };

  getHeatmapData = () => {
    return {
      id: "heatmap",
      type: "heatmap",
      source: "downloads",
      layout: { visibility: "none" },
      paint: {
        "heatmap-weight": ["interpolate", ["linear"], ["get", "count"], 0, 0, 1, 1, 100, 3],
        "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 3, 1, 6, 2],
        "heatmap-color": [
          "interpolate",
          ["linear"],
          ["heatmap-density"],
          0,
          "rgba(33,102,172,0)",
          0.2,
          "rgb(103,169,207)",
          0.4,
          "rgb(209,229,240)",
          0.6,
          "rgb(253,219,199)",
          0.8,
          "rgb(239,138,98)",
          1,
          "#ea404d",
        ],
        "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 3, 6, 6, 18],
      },
    };
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.filters, nextProps.filters)) {
      this.fetchNewData(undefined, nextProps);
    }
    if (this.props.stats !== nextProps.stats && this.getData(nextProps).length)
      this.updateSource(nextProps);
  }

  updateSource = nextProps => {
    const source = this.map.getSource("downloads");
    const sourceData = this.sourceData(nextProps);
    if (source) {
      source.setData(sourceData);
    }

    const clusterSource = this.map.getSource("downloads-cluster");
    if (clusterSource) {
      clusterSource.setData(sourceData);
    }
  };

  sourceData = (props = this.props) => {
    return {
      type: "FeatureCollection",
      features: this.getFeatures(props),
    };
  };

  getFeatures = (props = this.props) => {
    return this.getData(props).map(({ location, count }) => {
      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [location.lon, location.lat],
        },
        properties: {
          count: count,
        },
      };
    });
  };

  newGeoRequestID = () => {
    this.statsGeoRequestID = uuid();
    return this.statsGeoRequestID;
  };

  getData = (props = this.props) => {
    return get(props, ["stats", this.statsGeoRequestID], []);
  };

  componentWillUnmount() {
    this.map.remove();
  }

  handleToggleClick = val => {
    this.setState({ toggleValue: val });
    const optionToLayerMap = {
      heatmap: ["heatmap"],
      cluster: ["cluster-circle", "cluster-total-circle", "cluster-count", "cluster-total-count"],
    };
    Object.keys(optionToLayerMap).forEach(key => {
      optionToLayerMap[key].forEach(layerName => {
        this.map.setLayoutProperty(layerName, "visibility", val === key ? "visible" : "none");
      });
    });
  };
  render() {
    const style = {
      width: "100%",
      height: "100%",
      position: "relative",
    };

    return (
      <div style={style}>
        <div className={"mapbox__map-toggles"}>
          <ToggleButtonGroup
            type="radio"
            name="map-options"
            bsSize="xsmall"
            onChange={this.handleToggleClick}
            value={this.state.toggleValue}>
            <ToggleButton value={"cluster"} variant="secondary">
              Clusters
            </ToggleButton>
            <ToggleButton value={"heatmap"} variant="secondary">
              Heatmap
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <div style={style} ref={el => (this.mapContainer = el)} />
      </div>
    );
  }
}

export default connect(({ stats }) => ({ stats: stats.stats }), { fetchGeoStats })(Map);
